import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { isGroup, navSectionsForRole, type NavItem, type NavLeaf } from '@/config/nav';
import { cn } from '@/lib/utils';
import { useSession } from '@/stores/session';

/** Heading sections condensed to a single rail icon (all-leaf sections that
 *  would otherwise flood the rail with one icon per entry). */
const CONDENSED_SECTIONS = new Set(['My Space']);

const railButton = (active: boolean) =>
  cn(
    'grid size-10 place-items-center rounded-md transition-colors',
    'hover:bg-accent hover:text-accent-foreground',
    active ? 'bg-accent text-accent-foreground' : 'text-muted-foreground',
  );

function isPathActive(pathname: string, to: string): boolean {
  return to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(to + '/');
}

function RailLink({ leaf, pathname }: { leaf: NavItem; pathname: string }) {
  const Icon = leaf.icon;
  return (
    <NavLink
      to={leaf.to}
      className={railButton(isPathActive(pathname, leaf.to))}
      aria-label={leaf.label}
      title={leaf.label}
    >
      <Icon className="size-4.5" />
    </NavLink>
  );
}

/** A rail icon that opens a right-side flyout listing its sub-pages. */
function RailFlyout({
  label,
  icon: Icon,
  items,
  pathname,
  active,
}: {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  items: NavLeaf[];
  pathname: string;
  active: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className={railButton(active)}
        aria-label={label}
        title={label}
      >
        <Icon className="size-4.5" />
      </PopoverTrigger>
      <PopoverContent side="right" align="start" sideOffset={8} className="w-52 p-1.5">
        <p className="text-muted-foreground px-2 pt-1 pb-1.5 text-xs font-semibold tracking-wide uppercase">
          {label}
        </p>
        <div className="flex flex-col gap-0.5">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              className={cn(
                'rounded-md px-2 py-1.5 text-sm transition-colors',
                'hover:bg-accent hover:text-accent-foreground',
                isPathActive(pathname, item.to)
                  ? 'bg-accent text-accent-foreground font-medium'
                  : 'text-muted-foreground',
              )}
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Collapsed-sidebar icon rail (Plane-style): leaves are icon links with
 *  tooltips; module groups (and the condensed My Space section) open a flyout
 *  with their sub-pages, so everything stays one click away. */
export function SidebarRail() {
  const role = useSession((s) => s.role);
  const sections = navSectionsForRole(role);
  const { pathname } = useLocation();

  return (
    <ScrollArea className="h-full">
      <nav className="flex flex-col items-center gap-1 p-2" aria-label="Primary">
        {sections.map((section, i) => {
          const condensed =
            section.heading !== undefined &&
            CONDENSED_SECTIONS.has(section.heading) &&
            section.entries.every((e) => !isGroup(e));
          return (
            <div
              key={section.heading ?? `section-${i}`}
              className={cn(
                'flex flex-col items-center gap-1',
                i > 0 && 'border-sidebar-border mt-1 w-full border-t pt-2',
              )}
            >
              {condensed ? (
                <RailFlyout
                  label={section.heading!}
                  icon={(section.entries[0] as NavItem).icon}
                  items={section.entries as NavItem[]}
                  pathname={pathname}
                  active={section.entries.some(
                    (e) => !isGroup(e) && isPathActive(pathname, e.to),
                  )}
                />
              ) : (
                section.entries.map((entry) =>
                  isGroup(entry) ? (
                    <RailFlyout
                      key={entry.label}
                      label={entry.label}
                      icon={entry.icon}
                      items={entry.children}
                      pathname={pathname}
                      active={isPathActive(pathname, entry.basePath)}
                    />
                  ) : (
                    <RailLink key={entry.to} leaf={entry} pathname={pathname} />
                  ),
                )
              )}
            </div>
          );
        })}
      </nav>
    </ScrollArea>
  );
}
